import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  ShowChart as MeterIcon,
  Warning as WarningIcon,
  Work as WorkIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandMore as ExpandMoreIcon,
  ReportProblemOutlined as ComplaintIcon,
} from "@mui/icons-material";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import { formatAccountCalendarDate } from "../utils/formatAccountCalendarDate";
import {
  hasActivePaymentPlan,
  getPaymentPlanToggleMismatch,
  getPaymentPlanMismatchMessage,
} from "../utils/paymentPlanUtils";
import AccountControlsCard from "./AccountControlsCard";
import { DIALOGS } from "../hooks/useDialogManager";
import Gate from "components/Gate";
import { can } from "modules/settings/accessRoles";

const AccountInfoSection = React.memo(function AccountInfoSection({
  expanded,
  onToggle,
  openDialog,
}) {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);
  const payments = useSelector((state) => state.payments);
  const user = useSelector((state) => state.user);

  const acct = account.data.account;
  const paymentPlanItems = payments.paymentPlans?.items || [];
  const hasActivePlan = !payments.isLoading && hasActivePaymentPlan(paymentPlanItems);
  const paymentPlanMismatch = !payments.isLoading
    ? getPaymentPlanToggleMismatch(acct?.paymentPlan, paymentPlanItems)
    : null;
  const paymentPlanMismatchMessage = getPaymentPlanMismatchMessage(paymentPlanMismatch);

  const actionButtonSx = {
    minWidth: "auto",
    px: 2,
    py: 0.5,
    fontSize: "0.75rem",
    textTransform: "none",
    fontWeight: 500,
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      elevation={1}
      sx={{
        borderRadius: 2,
        "&:before": { display: "none" },
        boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "grey.50",
          borderRadius: "8px 8px 0 0",
          "&.Mui-expanded": { borderRadius: "8px 8px 0 0" },
        }}
      >
        <Box display="flex" alignItems="center" gap={2} sx={{ width: "100%" }}>
          <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h5" fontWeight="600">
              Account & Property Information
            </Typography>
            {!account.isLoading && acct && (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {Boolean(acct?.printedBills) && <Chip label="Printed Bills" size="small" color="primary" variant="outlined" />}
                {Boolean(acct?.eBills) && <Chip label="E-Bills" size="small" color="primary" variant="outlined" />}
                {Boolean(acct?.disconnected) && <Chip label="Disconnected" size="small" color="error" variant="outlined" />}
                {Boolean(acct?.stopPayment) && <Chip label="Stop Payment" size="small" color="warning" variant="outlined" />}
                {Boolean(acct?.bankruptcy) && <Chip label="Bankruptcy" size="small" color="error" variant="outlined" />}
                {Boolean(acct?.inCollections) && <Chip label="In Collections" size="small" color="error" variant="outlined" />}
                {Boolean(acct?.winterCrisis) && <Chip label="Winter Crisis" size="small" color="warning" variant="outlined" />}
                {Boolean(acct?.pledgeAccount) && <Chip label="Pledge Account" size="small" color="info" variant="outlined" />}
                {Boolean(acct?.medForm) && <Chip label="Med Form" size="small" color="info" variant="outlined" />}
                {hasActivePlan && (
                  <Chip label="Payment Plan" size="small" color="success" variant="outlined" />
                )}
                {paymentPlanMismatchMessage && (
                  <Tooltip title={paymentPlanMismatchMessage}>
                    <Chip
                      label="Payment Plan Mismatch"
                      size="small"
                      color="warning"
                      variant="outlined"
                      icon={<WarningIcon fontSize="small" />}
                    />
                  </Tooltip>
                )}
                {Boolean(acct?.autoPayments) && (
                  !payments.isLoading && !(payments.accountAutoPayments?.items?.length > 0) ? (
                    <Tooltip title="Auto-Payments is ON but no linked payment method was found.">
                      <Chip
                        label="Auto Payments"
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningIcon fontSize="small" />}
                      />
                    </Tooltip>
                  ) : (
                    <Chip label="Auto Payments" size="small" color="success" variant="outlined" />
                  )
                )}
                {account.data.forwardingAddress && account.data.forwardingAddress.length > 0 && (
                  <Chip label="Forwarding Address" size="small" color="info" variant="outlined" />
                )}
              </Box>
            )}
          </Box>
          {!account.isLoading && acct && (
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <Gate capability="editRecords">
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={(e) => { e.stopPropagation(); openDialog(DIALOGS.EDIT_ACCOUNT); }}
                sx={{ ...actionButtonSx, color: "primary.main", borderColor: "primary.main", "&:hover": { backgroundColor: "primary.50", borderColor: "primary.dark" } }}
              >
                Edit Account
              </Button>
              </Gate>
              {can(user.details, "moves") && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SwapHorizIcon />}
                  onClick={(e) => { e.stopPropagation(); openDialog(DIALOGS.TRANSFER_UNIT); }}
                  sx={{ ...actionButtonSx, color: "primary.main", borderColor: "primary.main", "&:hover": { backgroundColor: "primary.50", borderColor: "primary.dark" } }}
                >
                  Change Account Unit
                </Button>
              )}
              {(() => {
                const meters = account.data.unit?.meters ?? [];
                if (meters.length === 0) {
                  return (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MeterIcon />}
                      onClick={(e) => { e.stopPropagation(); openDialog(DIALOGS.METER_READINGS); }}
                      sx={actionButtonSx}
                    >
                      Meters
                    </Button>
                  );
                }
                return meters.map((meter) => (
                  <Button
                    key={meter.id ?? meter.uuid ?? meter.meterNum}
                    variant="outlined"
                    size="small"
                    startIcon={<MeterIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDialog(DIALOGS.METER_READINGS, { selectedMeterId: meter.id });
                    }}
                    sx={actionButtonSx}
                  >
                    Meter #{meter.meterNum}
                  </Button>
                ));
              })()}
              <Button variant="outlined" size="small" startIcon={<WorkIcon />}
                onClick={(e) => { e.stopPropagation(); openDialog(DIALOGS.WORK_ORDERS); }}
                sx={actionButtonSx}>
                Work Orders
              </Button>
              <Button variant="outlined" size="small" startIcon={<ComplaintIcon />}
                onClick={(e) => { e.stopPropagation(); openDialog(DIALOGS.COMPLAINTS); }}
                sx={actionButtonSx}>
                Complaints
              </Button>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "stretch" }}>
          {/* Customer Details */}
          <Card variant="outlined" sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon color="primary" fontSize="small" />
              Customer Details
            </Typography>
            {account.isLoading ? (
              <Stack spacing={2}>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />)}</Stack>
            ) : (
              <Stack spacing={2} sx={{ flex: 1 }}>
                <DetailRow label="Account Number" value={acct?.accountNum || "N/A"} />
                <DetailRow
                  label="Customer Name"
                  value={acct?.firstName && acct?.lastName ? `${acct.firstName} ${acct.lastName}` : acct?.lastName || "N/A"}
                />
                <DetailRow label="Email" value={acct?.email || "N/A"} />
                <DetailRow label="Phone" value={acct?.phone || acct?.phone1 || "N/A"} />
              </Stack>
            )}
          </Card>

          {/* Property & Unit Details */}
          <Card variant="outlined" sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <HomeIcon color="secondary" fontSize="small" />
              Property & Unit Details
            </Typography>
            {account.isLoading ? (
              <Stack spacing={2}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />)}</Stack>
            ) : (
              <Stack spacing={2} sx={{ flex: 1 }}>
                {account.data.unit && (account.data.unit.address1 || account.data.unit.address2) && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Service Address:</Typography>
                    <Typography
                      variant="body2"
                      fontWeight="500"
                      sx={{
                        textAlign: "right", maxWidth: "60%", opacity: 0.9, cursor: "pointer",
                        "&:hover": { opacity: 1, textDecoration: "underline" },
                      }}
                      onClick={() => {
                        const unit = account.data.unit;
                        let addr = "";
                        if (unit.address1) addr += unit.address1;
                        if (unit.address2) addr += (addr ? ", " : "") + unit.address2;
                        if (unit.city) addr += (addr ? ", " : "") + unit.city;
                        if (unit.state) addr += (addr ? ", " : "") + unit.state;
                        if (unit.zip) addr += (addr ? " " : "") + unit.zip;
                        if (addr) {
                          navigator.clipboard.writeText(addr);
                          dispatch(setSnackbar(true, "success", "Service address copied to clipboard"));
                        }
                      }}
                      title="Click to copy service address"
                    >
                      {(() => {
                        const unit = account.data.unit;
                        let addr = "";
                        if (unit.address1) addr += unit.address1;
                        if (unit.address2) addr += (addr ? ", " : "") + unit.address2;
                        if (unit.city) addr += (addr ? ", " : "") + unit.city;
                        if (unit.state) addr += (addr ? ", " : "") + unit.state;
                        if (unit.zip) addr += (addr ? " " : "") + unit.zip;
                        return addr || "N/A";
                      })()}
                    </Typography>
                  </Box>
                )}
                <DetailRow
                  label="Property Manager"
                  value={account.data?.property?.propertyManager?.name || "N/A"}
                />
                {account.data.unit?.meters?.length > 0 && (
                  <DetailRow
                    label="Meter(s)"
                    value={account.data.unit.meters.map((m) => m.meterNum).filter(Boolean).join(", ") || "N/A"}
                  />
                )}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Move In Date:</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {(() => {
                      if (acct?.moveInDateOverride) return formatAccountCalendarDate(acct.moveInDateOverride);
                      if (account.data.move?.moveIn?.[0]?.moveInDate) return formatAccountCalendarDate(account.data.move.moveIn[0].moveInDate);
                      if (acct?.moveInDate) return formatAccountCalendarDate(acct.moveInDate);
                      return "N/A";
                    })()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Move Out Date:</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {(() => {
                      const rawMoveOut = account.data.move?.moveOut;
                      const moveOut = Array.isArray(rawMoveOut) ? rawMoveOut[0] : rawMoveOut;
                      if (acct?.moveOutDateOverride) return formatAccountCalendarDate(acct.moveOutDateOverride);
                      if (moveOut?.moveOutDate) {
                        const isPending = moveOut.processed == 0 || moveOut.processed === false;
                        if (isPending) return `Pending Move-out: ${formatAccountCalendarDate(moveOut.moveOutDate)}`;
                        return formatAccountCalendarDate(moveOut.moveOutDate);
                      }
                      if (acct?.moveOutDate) return formatAccountCalendarDate(acct.moveOutDate);
                      return "N/A";
                    })()}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Card>

          {/* Account Controls */}
          <AccountControlsCard openDialog={openDialog} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

function DetailRow({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">{label}:</Typography>
      <Typography variant="body2" fontWeight="500">{value}</Typography>
    </Box>
  );
}

export default AccountInfoSection;
